/*
 * business-card/pdf.js — the file a print shop accepts without an email back.
 *
 * WHAT MAKES THIS DIFFERENT FROM "EXPORT TO PDF"
 * ─────────────────────────────────────────────
 * Four things, and each one is a reason files get rejected:
 *
 *  1. IT IS ACTUALLY CMYK. Every colour written here is a `k` operator —
 *     DeviceCMYK — not `rg`. An RGB card is not a card that prints slightly
 *     differently; it is a file the shop has to convert with a profile it
 *     guesses, which is where "на экране был яркий синий" comes from. See
 *     ink.js for why the numbers are trustworthy and why no ICC profile is
 *     attached (RU shops ask for none, explicitly).
 *
 *  2. THE PAGE BOXES ARE REAL. MediaBox ⊃ BleedBox ⊃ TrimBox, with the trim at
 *     exactly 90 × 50 (or whatever the format says). Automated preflight at the
 *     shop reads those boxes; a PDF that is merely "94 mm wide and we promise
 *     there is 2 mm of bleed" fails it, because nothing in the file says where
 *     the cut goes.
 *
 *  3. TEXT CAN BE OUTLINES. "Все шрифты в кривые" is a written requirement at
 *     most RU/CIS shops. Curves are the DEFAULT here for that reason, with
 *     embedded subsets available for anyone who wants selectable text.
 *
 *  4. IT KNOWS ABOUT PAPER. Crop marks sit outside the bleed where a guillotine
 *     operator expects them, and the home-printing sheet carries a printed
 *     50 mm scale bar so the driver's silent "fit to page" can be caught with a
 *     ruler instead of discovered on the fortieth card.
 *
 * COORDINATES. Everything upstream is millimetres, y DOWN from the TRIM
 * top-left, because that is how a card is described. PDF is points, y UP from
 * the page's bottom-left, and the page is bigger than the trim. Both flips
 * happen in `frame()` and nowhere else — one place to get right, one to test.
 *
 * ⚠️ KERNING. pdf-lib's `drawText` lays glyphs out UNKERNED; fontkit's
 * `layout()` — which is what the outline path and the on-screen preview both
 * use — kerns. Measured 2026-08-23: up to 1.08 pt on 11 pt Playfair. So the
 * two export modes are not glyph-identical, and the embedded-font path
 * re-measures each line with pdf-lib's own metrics before aligning it. Sharing
 * one width for both would push right-aligned text 0.4 mm past where the
 * preview showed it.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardPdf = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var PT = 72 / 25.4;                    // exact: points per millimetre
    /* Crop marks live between the bleed box and the media box. 5 mm of margin
     * holds a 3 mm mark plus the 2 mm gap that keeps the mark from printing
     * into the artwork if the cut drifts outward. */
    var MARK_MARGIN_MM = 5;
    var MARK_LEN_MM = 3;
    var MARK_GAP_MM = 2;

    var _pdfLib = null, _qrgen = null;

    function assetChainFor(name, fallbackPath) {
        var cfg = (window.__BUSINESS_CARD__ && window.__BUSINESS_CARD__.assets) || {};
        var list = cfg[name];
        return (list && list.length) ? list : [fallbackPath];
    }

    /* Both libraries ARE the export — no pdf-lib, no PDF; no qrcode-generator,
     * no QR. So they go through the shared asset chain: OUR origin first on
     * every locale, an external mirror only as a fallback. A `cdn()` call would
     * pick by LOCALE and send a Russian visitor reading the English page to a
     * host that is unreachable there — a dead tool, not a slower one. */
    function loadChain(name, fallbackPath, probe, label) {
        if (probe()) return Promise.resolve(probe());
        var urls = assetChainFor(name, fallbackPath);
        var p = (window.__assetChain && window.__assetChain.loadScript)
            ? window.__assetChain.loadScript(urls, label)
            : new Promise(function (resolve, reject) {
                var s = document.createElement('script');
                s.src = urls[0]; s.async = true;
                s.onload = resolve;
                s.onerror = function () { reject(new Error(label + ' failed to load')); };
                document.head.appendChild(s);
            });
        return p.then(function () {
            var got = probe();
            if (!got) throw new Error(label + ' loaded but absent');
            return got;
        }).catch(function (e) {
            if (e && !e.phase) e.phase = 'script';
            throw e;
        });
    }
    function ensurePdfLib() {
        if (_pdfLib) return Promise.resolve(_pdfLib);
        return loadChain('pdfLib', '/vendor/pdf-lib/pdf-lib.min.js',
            function () { return window.PDFLib; }, 'pdf-lib')
            .then(function (l) { _pdfLib = l; return l; });
    }
    /* qrcode-generator, not bwip-js. Three reasons, in order of weight:
     *
     *  1. It exposes the MODULE MATRIX (`isDark`), so the QR goes into the PDF
     *     as vector rectangles in DeviceCMYK. bwip-js hands back a canvas, which
     *     can only be embedded as an RGB raster — one RGB object in an otherwise
     *     pure-CMYK file, and a symbol whose edges are at the mercy of the
     *     printer's resampling. A QR is the one graphic where a soft edge is a
     *     functional failure, not an aesthetic one.
     *  2. It reports the REAL module count, so the mm/module warning stops being
     *     an estimate from a capacity table and becomes a measurement.
     *  3. 20 KB against 1.1 MB.
     *
     * ⚠️ The library's own default `stringToBytes` is Latin-1 and silently
     * mangles Cyrillic — "Петров" encodes as garbage into a symbol that scans
     * perfectly. Our vendored build already appends the UTF-8 override, and
     * `ensureQr` re-asserts it so a future vendor refresh cannot quietly undo
     * it. Measured 2026-08-23: default mode round-trips to "\u001F5B@>2".
     */
    function ensureQr() {
        if (_qrgen) return Promise.resolve(_qrgen);
        return loadChain('qrcode', '/vendor/qrcode/qrcode.min.js',
            function () { return window.qrcode; }, 'qrcode-generator')
            .then(function (l) {
                if (l.stringToBytesFuncs && l.stringToBytesFuncs['UTF-8']) {
                    l.stringToBytes = l.stringToBytesFuncs['UTF-8'];
                }
                _qrgen = l; return l;
            });
    }

    /* Module matrix for a payload. `ecc` is one of L/M/Q/H; version 0 lets the
     * library pick the smallest that fits. */
    function qrMatrix(qr, text, ecc) {
        var q = qr(0, ecc || 'M');
        q.addData(String(text == null ? '' : text));
        q.make();
        var n = q.getModuleCount();
        var rows = [];
        for (var r = 0; r < n; r++) {
            var row = [];
            for (var c = 0; c < n; c++) row.push(q.isDark(r, c));
            rows.push(row);
        }
        return { n: n, rows: rows };
    }

    /* One SVG path covering every dark module, in a 0..(n+8) unit grid that
     * already includes the mandatory 4-module quiet zone.
     *
     * Runs of horizontally adjacent modules are merged into one rectangle. It
     * is not a size optimisation — abutting rectangles in a PDF can show a
     * hairline seam where the rasteriser rounds two edges to different pixels,
     * and on a QR that seam is inside a module. Merging removes the seam by
     * removing the join. */
    function qrPath(m) {
        var q = 4, out = [];
        for (var r = 0; r < m.n; r++) {
            var c = 0;
            while (c < m.n) {
                if (!m.rows[r][c]) { c++; continue; }
                var start = c;
                while (c < m.n && m.rows[r][c]) c++;
                var w = c - start;
                out.push('M' + (start + q) + ' ' + (r + q) +
                         'h' + w + 'v1h-' + w + 'Z');
            }
        }
        return { d: out.join(''), units: m.n + 2 * q };
    }

    /* ── colour ─────────────────────────────────────────────────────────────
     * One function, and it only speaks CMYK. There is deliberately no rgb()
     * escape hatch: the moment one exists, one element uses it and the file is
     * mixed-colourspace without anybody noticing. */
    function ink(PL, c) {
        if (!c) return PL.cmyk(0, 0, 0, 1);
        return PL.cmyk(clamp01(c.c), clamp01(c.m), clamp01(c.y), clamp01(c.k));
    }
    function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : (v || 0)); }

    /* ── the coordinate flip, in one place ──────────────────────────────────
     * `origin` is where the TRIM box starts inside the page, in mm from the
     * page's top-left. Callers work in trim space and never see the page. */
    function frame(pageHmm, origin) {
        return {
            x: function (mm) { return (origin.x + mm) * PT; },
            /* `h` is the height of the thing being placed: pdf-lib anchors
             * rectangles and images at their BOTTOM-left, our model at the top. */
            y: function (mm, h) { return (pageHmm - origin.y - mm - (h || 0)) * PT; },
            len: function (mm) { return mm * PT; },
        };
    }

    /* ── page geometry ──────────────────────────────────────────────────────*/
    function pageGeom(boxes, opt) {
        var extra = opt.marks ? MARK_MARGIN_MM : 0;
        var pw = boxes.w + 2 * boxes.bleed + 2 * extra;
        var ph = boxes.h + 2 * boxes.bleed + 2 * extra;
        return {
            w: pw, h: ph, extra: extra,
            origin: { x: boxes.bleed + extra, y: boxes.bleed + extra },
            bleed: boxes.bleed,
            trimW: boxes.w, trimH: boxes.h,
        };
    }

    /* ── crop marks ─────────────────────────────────────────────────────────
     * Four corners, two marks each, drawn in 100 % K only. Registration black
     * (CMYK all-100) is traditional for marks on a colour press, but on the
     * digital and small-offset work a business card actually goes through it
     * just adds three plates of ink outside the trim for no benefit — and it
     * pushes the sheet's coverage up. K-only is the safe modern choice.
     *
     * Marks start `MARK_GAP_MM` OUTSIDE the bleed box, never touching it: a
     * mark drawn into the bleed can end up inside the card when the cut drifts
     * outward, which is a black line on a finished card.
     */
    function drawMarks(page, PL, g) {
        var col = PL.cmyk(0, 0, 0, 1);
        var t = 0.25 * PT;
        var b = g.bleed, gap = MARK_GAP_MM, len = MARK_LEN_MM;
        // Trim edges in page mm (from page top-left)
        var L = g.origin.x, R = g.origin.x + g.trimW;
        var T = g.origin.y, B = g.origin.y + g.trimH;
        var pxs = [
            /* vertical marks above and below each vertical trim line */
            [L, T - b - gap - len, L, T - b - gap], [L, B + b + gap, L, B + b + gap + len],
            [R, T - b - gap - len, R, T - b - gap], [R, B + b + gap, R, B + b + gap + len],
            /* horizontal marks left and right of each horizontal trim line */
            [L - b - gap - len, T, L - b - gap, T], [R + b + gap, T, R + b + gap + len, T],
            [L - b - gap - len, B, L - b - gap, B], [R + b + gap, B, R + b + gap + len, B],
        ];
        pxs.forEach(function (m) {
            page.drawLine({
                start: { x: m[0] * PT, y: (g.h - m[1]) * PT },
                end:   { x: m[2] * PT, y: (g.h - m[3]) * PT },
                thickness: t, color: col,
            });
        });
    }

    /* ── the drawing of one side ────────────────────────────────────────────*/
    function drawItems(page, PL, f, items, res) {
        var i;
        for (i = 0; i < items.length; i++) {
            var it = items[i];
            switch (it.type) {
                case 'rect':  drawRect(page, PL, f, it); break;
                case 'frame': drawFrame(page, PL, f, it); break;
                case 'poly':  drawPoly(page, PL, f, it); break;
                case 'line':  drawLine(page, PL, f, it); break;
                case 'icon':  drawIcon(page, PL, f, it); break;
                case 'text':  drawText(page, PL, f, it, res); break;
                case 'photo': drawPhoto(page, PL, f, it, res); break;
                case 'image': drawImage(page, PL, f, it, res); break;
                case 'qr':    drawQr(page, PL, f, it, res); break;
                case 'path':  drawPath(page, PL, f, it); break;
            }
        }
    }

    function drawRect(page, PL, f, it) {
        var o = {
            x: f.x(it.x), y: f.y(it.y, it.h),
            width: f.len(it.w), height: f.len(it.h),
            color: ink(PL, it.color),
        };
        /* pdf-lib has no rounded rectangle; a radius becomes an SVG path. Only
         * paid for when actually asked, because the path form is a dozen times
         * the operators of a plain `re`. */
        if (it.radius > 0.01) {
            window.__pdfSvgPath(page, roundRectPath(it.w, it.h, it.radius), {
                x: f.x(it.x), y: f.y(it.y), color: ink(PL, it.color), scale: PT,
            });
            return;
        }
        page.drawRectangle(o);
    }

    function roundRectPath(w, h, r) {
        r = Math.min(r, w / 2, h / 2);
        return 'M' + r + ' 0' +
            'H' + (w - r) + 'A' + r + ' ' + r + ' 0 0 1 ' + w + ' ' + r +
            'V' + (h - r) + 'A' + r + ' ' + r + ' 0 0 1 ' + (w - r) + ' ' + h +
            'H' + r + 'A' + r + ' ' + r + ' 0 0 1 0 ' + (h - r) +
            'V' + r + 'A' + r + ' ' + r + ' 0 0 1 ' + r + ' 0Z';
    }

    function drawFrame(page, PL, f, it) {
        page.drawRectangle({
            x: f.x(it.x), y: f.y(it.y, it.h),
            width: f.len(it.w), height: f.len(it.h),
            borderColor: ink(PL, it.color),
            borderWidth: f.len(it.stroke),
        });
    }

    function drawPoly(page, PL, f, it) {
        var p = it.points;
        var d = 'M' + p.map(function (q) { return q[0] + ' ' + q[1]; }).join('L') + 'Z';
        window.__pdfSvgPath(page, d, { x: f.x(0), y: f.y(0), color: ink(PL, it.color), scale: PT });
    }

    function drawLine(page, PL, f, it) {
        page.drawLine({
            start: { x: f.x(it.x), y: f.y(it.y) },
            end:   { x: f.x(it.x + it.w), y: f.y(it.y + (it.h || 0)) },
            thickness: f.len(it.stroke), color: ink(PL, it.color),
        });
    }

    /* A free vector path in trim millimetres (see render.js drawPath). The path
     * is scaled by PT in one place, so the stroke width is given in the path's
     * own units — millimetres — exactly as the canvas draws it. */
    function drawPath(page, PL, f, it) {
        if (!it.d) return;
        var o = { x: f.x(0), y: f.y(0), scale: PT };
        if (it.fill) o.color = ink(PL, it.fill);
        if (it.stroke && it.sw > 0) {
            o.borderColor = ink(PL, it.stroke);
            o.borderWidth = it.sw;
            if (it.cap === 'round' && PL.LineCapStyle) o.borderLineCap = PL.LineCapStyle.Round;
        }
        if (!o.color && !o.borderColor) return;
        if (it.opacity != null && it.opacity < 1) { o.opacity = it.opacity; o.borderOpacity = it.opacity; }
        window.__pdfSvgPath(page, it.d, o, PL);
    }

    function drawIcon(page, PL, f, it) {
        /* Icons are authored in a 24×24 box as STROKE paths; scale to the
         * requested size. `borderWidth` is applied in the path's own units —
         * pdf-lib emits `w` AFTER the scale matrix — so the millimetre stroke
         * has to be divided back out. Getting that backwards makes the icons
         * either invisible or ink blots, and only at print size. */
        window.__pdfSvgPath(page, it.path, {
            x: f.x(it.x), y: f.y(it.y),
            borderColor: ink(PL, it.color),
            borderWidth: (it.stroke || it.size * 0.07) / (it.size / 24),
            borderLineCap: PL.LineCapStyle ? PL.LineCapStyle.Round : undefined,
            scale: (it.size / 24) * PT,
        });
    }

    function drawText(page, PL, f, it, res) {
        var col = ink(PL, it.color);
        /* Tracking is a fraction of the type size, and it is a fraction of an
         * em on screen too — the preview steps by `track * size`. It used to be
         * passed to the outline path and dropped entirely by the font path, so
         * the same card printed two different widths depending on a checkbox. */
        var trackMm = (it.track || 0) * it.size;

        for (var li = 0; li < it.lines.length; li++) {
            var lineText = it.lines[li];
            if (!lineText) continue;
            var baseY = it.y + li * it.lineHeight;

            /* Pieces in reading order, each with a face that can actually write
               it. A Latin line is ONE piece and everything below collapses to
               what it was. */
            var pieces = runsOf(it, opts_of(res), lineText).map(function (r) {
                if (res.mode === 'curves') {
                    var o = res.outline(r.text, it, r.faceId);
                    /* outlineWith already put a gap after every glyph, last one
                       included, so its width IS the step to the next piece. */
                    return { r: r, o: o, adv: o ? o.width : 0 };
                }
                var fnt = res.font(it, r.faceId);
                /* ⚠️ MILLIMETRES. `widthOfTextAtSize(text, sizeInMm)` already
                 * answers in millimetres; the old code divided it by PT as
                 * well and got a width 2.83× too small. Nothing threw — a
                 * centred line simply sat about 11 mm right of centre on a
                 * 90 mm card, and only in this export mode. Measured against
                 * the outline path, which had it right: 33.88 mm vs 11.95. */
                return { r: r, font: fnt,
                         adv: (fnt ? fnt.widthOfTextAtSize(r.text, it.size) : 0)
                              + trackMm * Array.from(r.text).length };
            });

            var total = pieces.reduce(function (a, P) { return a + P.adv; }, 0)
                      - (trackMm ? trackMm : 0);     // the last gap is advance, not ink
            var x = alignX(it, total);

            for (var pi = 0; pi < pieces.length; pi++) {
                var P = pieces[pi];
                if (res.mode === 'curves') {
                    if (P.o) window.__pdfSvgPath(page, P.o.path, { x: f.x(x), y: f.y(baseY), color: col, scale: PT });
                } else if (P.font) {
                    if (trackMm) {
                        page.pushOperators(PL.pushGraphicsState(), PL.setCharacterSpacing(trackMm * PT));
                    }
                    page.drawText(P.r.text, {
                        x: f.x(x), y: f.y(baseY), size: it.size * PT,
                        font: P.font, color: col,
                    });
                    if (trackMm) page.pushOperators(PL.popGraphicsState());
                }
                x += P.adv;
            }
        }
    }

    /* The exporter carries its options on `res` so drawText can resolve a face
       without threading them through every call site. */
    function opts_of(res) { return res.opts || {}; }

    function alignX(it, width) {
        if (it.align === 'center') return it.x + (it.w - width) / 2;
        if (it.align === 'right')  return it.x + it.w - width;
        return it.x;
    }

    /* A baked background is an ordinary DeviceCMYK image XObject — the same
     * object the colour-logo path builds, so a photo card is still a file whose
     * every pixel is CMYK and whose preflight at the print shop passes. It is
     * keyed by the bake key rather than by the source file, because two sides
     * may carry the same photograph with different blur. */
    function drawPhoto(page, PL, f, it, res) {
        var img = res.images[it.pkey];
        if (!img) return;
        if (img.cmyk) { drawCmykImage(page, PL, f, it, img); return; }
        if (img.embedded) {
            page.drawImage(img.embedded, {
                x: f.x(it.x), y: f.y(it.y, it.h),
                width: f.len(it.w), height: f.len(it.h),
            });
        }
    }

    function drawImage(page, PL, f, it, res) {
        var img = res.images[it.src];
        if (!img) return;
        if (img.stencil) {
            /* A one-colour logo drawn as a PDF stencil mask: the bitmap says
             * WHERE, the current fill colour says WHAT — and that colour is
             * DeviceCMYK. This is the only way to put a raster logo into a
             * card without introducing an RGB object into an otherwise pure
             * CMYK file, and it is how mono logos are done in real print work. */
            drawStencil(page, PL, f, it, img);
            return;
        }
        if (img.cmyk) { drawCmykImage(page, PL, f, it, img); return; }
        page.drawImage(img.embedded, {
            x: f.x(it.x), y: f.y(it.y, it.h),
            width: f.len(it.w), height: f.len(it.h),
        });
    }

    /* A full-colour logo as a DeviceCMYK image XObject.
     *
     * pdf-lib's `embedPng` always writes /DeviceRGB, and one RGB object is
     * enough to make the file "not a CMYK PDF" for a print shop's preflight —
     * which is the one thing this tool promises. Built by hand for the same
     * reason the stencil is, and with the same trap: `flateStream` wants a
     * PLAIN object, never a `context.obj(...)` wrapper.
     *
     * Transparency rides along as an 8-bit /SMask, so a logo on a transparent
     * background still sits on the card rather than in a white box. */
    function drawCmykImage(page, PL, f, it, img) {
        var doc = page.doc;
        var key = '__cmyk_' + img.id;
        var ref = doc[key];
        if (!ref) {
            var dict = {
                Type: 'XObject', Subtype: 'Image',
                Width: img.w, Height: img.h,
                ColorSpace: 'DeviceCMYK', BitsPerComponent: 8,
            };
            if (img.alpha) {
                var sm = doc.context.register(doc.context.flateStream(img.alpha, {
                    Type: 'XObject', Subtype: 'Image',
                    Width: img.w, Height: img.h,
                    ColorSpace: 'DeviceGray', BitsPerComponent: 8,
                }));
                dict.SMask = sm;
            }
            ref = doc.context.register(doc.context.flateStream(img.samples, dict));
            doc[key] = ref;
        }
        var name = page.node.newXObject('CardCmyk', ref);
        page.pushOperators(
            PL.pushGraphicsState(),
            PL.translate(f.x(it.x), f.y(it.y, it.h)),
            PL.scale(f.len(it.w), f.len(it.h)),
            PL.drawObject(name),
            PL.popGraphicsState()
        );
    }

    /* The conversion itself lives in the ink model (`CardInk.cmykBitmap`), not
     * here: the PREVIEW has to run the same pixels through the same code, and
     * two copies of a colour model are two colour models. */
    function rgbaToCmyk(data, w, h) {
        var ink = window.CardInk;
        if (!ink || !ink.cmykBitmap) return null;
        return ink.cmykBitmap(data, w, h);
    }

    /* ImageMask XObject: 1 bit per sample, painted in the current fill colour.
     * Built by hand because pdf-lib has no API for it — `drawImage` always
     * makes a colour image. */
    function drawStencil(page, PL, f, it, img) {
        var doc = page.doc;
        var key = '__stencil_' + img.id;
        var ref = doc[key];
        if (!ref) {
            /* ⚠️ A PLAIN object, not `context.obj(...)`. `flateStream(bytes,
             * dict)` calls `context.obj(dict)` itself; handing it an already
             * built PDFDict makes it enumerate that WRAPPER's own JS fields, and
             * the file gets an object whose keys are `/dict` and `/context`
             * instead of `/Width` and `/ImageMask`. Measured 2026-08-23: the
             * PDF still saves, still opens, and the logo is simply absent —
             * exactly the class of failure this tool exists to stop. */
            var dict = {
                Type: 'XObject', Subtype: 'Image',
                Width: img.w, Height: img.h,
                ImageMask: true, BitsPerComponent: 1,
                Decode: [1, 0],
            };
            ref = doc.context.register(doc.context.flateStream(img.bits, dict));
            doc[key] = ref;
        }
        var name = page.node.newXObject('CardStencil', ref);
        var ops = PL.pushGraphicsState();
        page.pushOperators(
            PL.pushGraphicsState(),
            PL.setFillingCmykColor(clamp01(it.color ? it.color.c : 0), clamp01(it.color ? it.color.m : 0),
                                   clamp01(it.color ? it.color.y : 0), clamp01(it.color ? it.color.k : 1)),
            PL.translate(f.x(it.x), f.y(it.y, it.h)),
            PL.scale(f.len(it.w), f.len(it.h)),
            PL.drawObject(name),
            PL.popGraphicsState()
        );
        return ops;
    }

    function drawQr(page, PL, f, it, res) {
        var sym = res.qrSymbols[it.payload + '|' + it.ecc];
        if (!sym) return;
        /* The light field is painted explicitly rather than left as paper.
         * A QR over a coloured background with no light field has no contrast
         * for the reader, and the quiet zone is part of the symbol — it has to
         * be light, not "whatever the card is". */
        page.drawRectangle({
            x: f.x(it.x), y: f.y(it.y, it.size),
            width: f.len(it.size), height: f.len(it.size),
            color: ink(PL, it.light),
        });
        window.__pdfSvgPath(page, sym.d, {
            x: f.x(it.x), y: f.y(it.y),
            color: ink(PL, it.dark),
            scale: f.len(it.size) / sym.units,
        });
    }

    /* ── resource preparation ───────────────────────────────────────────────
     * Everything that needs the network or a canvas is resolved BEFORE any
     * drawing starts, so the draw pass is synchronous and its ordering is
     * obvious. A half-async draw loop is how z-order bugs appear only on slow
     * connections.
     */
    function prepare(doc, PL, sides, opts) {
        var texts = [];
        var qrs = {};
        var images = {};
        var photos = {};

        sides.forEach(function (S) {
            S.items.forEach(function (it) {
                if (it.type === 'text') texts.push(it);
                if (it.type === 'qr') qrs[it.payload + '|' + it.ecc] = it;
                /* One source, two placements: the same logo is usually on the
                 * front small and on the back large, and both sides share ONE
                 * embedded XObject. Whichever item won this assignment decided
                 * the rasterisation size for both — i.e. an arbitrary one.
                 * Keep the LARGEST: it is the only choice that cannot make the
                 * other side worse. */
                if (it.type === 'image') {
                    var prev = images[it.src];
                    if (!prev || (it.w || 0) > (prev.w || 0)) images[it.src] = it;
                }
                if (it.type === 'photo' && it.pkey) photos[it.pkey] = it;
            });
        });

        var jobs = [];
        var res = {
            mode: opts.text === 'font' ? 'font' : 'curves',
            /* carried so drawText can resolve a face per run without threading
               the options through every call site */
            opts: opts,
            images: {}, qrSymbols: {}, fonts: {},
        };

        /* QR symbols, as vector geometry. No canvas, no raster, no resampling. */
        var qrKeys = Object.keys(qrs);
        if (qrKeys.length) {
            jobs.push(ensureQr().then(function (qr) {
                qrKeys.forEach(function (k) {
                    var it = qrs[k];
                    res.qrSymbols[k] = qrPath(qrMatrix(qr, it.payload, it.ecc));
                });
            }));
        }

        /* Logos. */
        Object.keys(images).forEach(function (src) {
            var it = images[src];
            jobs.push(loadImageBytes(src, opts, it).then(function (r) {
                if (r.stencil) { res.images[src] = r; return; }
                if (r.cmyk) { res.images[src] = r; return; }
                var p = r.mime === 'image/jpeg' ? doc.embedJpg(r.bytes) : doc.embedPng(r.bytes);
                return p.then(function (emb) { res.images[src] = { embedded: emb }; });
            }));
        });

        /* ── backgrounds ────────────────────────────────────────────────────
         * Baked HERE, at print resolution, by the same `CardPhoto.bake` the
         * preview calls at screen resolution. The temptation is to reuse the
         * canvas the preview already made — it is sitting right there — and it
         * would ship a 380 px image as a 90 mm background: a card that looked
         * right on screen and printed at 107 dpi.
         *
         * 300 dpi is the press standard for continuous tone; going higher does
         * not add detail the source does not have (preflight says so
         * separately) and quadruples both the conversion and the file. */
        Object.keys(photos).forEach(function (k) {
            var it = photos[k];
            jobs.push(bakePhotoBytes(it).then(function (r) {
                if (r) res.images[k] = r;
            }));
        });

        /* Fonts / outlines. The coverage table is fetched once before either
         * path: `embed()` reads it synchronously and reports a missing face as
         * "unknown face: <id>", which looks like a bad id and is really a race
         * against the network. */
        /* ⚠️ `ready(text)` — NOT `load()`. The CJK half of the coverage table is
         * fetched separately and only on demand; asking for `load()` leaves
         * every Korean and Japanese glyph looking uncovered, which is not an
         * error anywhere — the face simply comes back as «none» and the card
         * prints without those words. */
        var allText = texts.map(function (it) { return (it.lines || []).join(''); }).join('');
        jobs.push(window.PdfTextFont.ready(allText).catch(function () {
            return window.PdfTextFont.load();
        }).catch(function (e) { throw faceFailed(e); }).then(function () {
            return res.mode === 'curves'
                ? prepareOutlines(texts, opts, res)
                : prepareFonts(doc, texts, opts, res);
        }));

        return Promise.all(jobs).then(function () { return res; });
    }


    /**
     * ⚠️ THE CHOSEN FACE IS NOT ALWAYS A FACE THAT CAN WRITE THE TEXT. The user
     * picks a typeface for its look — Montserrat, Oswald, Playfair — and those
     * are Latin (plus Cyrillic) designs. Handing an Arabic name to Montserrat
     * does not fail: it embeds happily and prints nothing readable, while the
     * canvas preview quietly falls back to a system Arabic font, so the screen
     * looked right. Measured 2026-08-24: `missing()` named all ten letters of
     * «مدرسة النهضة» for the tool's own default face, and the Arabic sample
     * this tool ships with went through that path.
     *
     * So the face is resolved PER RUN, by coverage, exactly as the certificate
     * exporter does it — `visualRuns` hands back the pieces already split by
     * direction and by face, and every face it names has to be embedded here.
     */
    function runsOf(it, opts, lineText) {
        return window.PdfTextFont.visualRuns(lineText, faceIdFor(it, opts));
    }

    function facesNeeded(texts, opts) {
        var need = {};
        texts.forEach(function (it) {
            var bold = it.weight >= 600;
            (it.lines || []).forEach(function (line) {
                if (!line) return;
                runsOf(it, opts, line).forEach(function (r) {
                    var id = r.faceId || faceIdFor(it, opts);
                    need[id + '|' + (bold ? 'b' : 'r')] = { id: id, bold: bold };
                });
            });
        });
        return need;
    }

    function keyOf(it, faceId, opts) {
        return (faceId || faceIdFor(it, opts)) + '|' + (it.weight >= 600 ? 'b' : 'r');
    }

    /* ⚠️ A FACE THAT DID NOT LOAD FAILS THE EXPORT. It used to become `null`
     * here, and every run drawn with it simply vanished: the PDF came out with
     * the logo and the frame and WITHOUT the name — no error, no toast — while
     * the preview (CSS faces) showed the text. Measured 01.10.2026 with the
     * font request refused: a 1-page card, 0 text paths. The person is told
     * the face did not arrive and can press again; the request is retried. */
    function faceFailed(cause) {
        var e = new Error('card face did not load: ' + ((cause && cause.message) || cause));
        e.phase = 'fonts';
        e.cause = cause;
        return e;
    }

    function prepareOutlines(texts, opts, res) {
        var PTF = window.PdfTextFont;
        var need = facesNeeded(texts, opts);
        var faces = {};
        return Promise.all(Object.keys(need).map(function (k) {
            return PTF.face(need[k].id, { bold: need[k].bold })
                .then(function (r) { faces[k] = r.font; })
                .catch(function (e) { throw faceFailed(e); });
        })).then(function () {
            res.outline = function (lineText, it, faceId) {
                var font = faces[keyOf(it, faceId, opts)] || faces[keyOf(it, null, opts)];
                if (!font) return null;
                return PTF.outlineWith(font, lineText, it.size, { track: it.track });
            };
            res.faces = faces;
        });
    }

    function prepareFonts(doc, texts, opts, res) {
        var PTF = window.PdfTextFont;
        var need = facesNeeded(texts, opts);
        var fonts = {};
        return Promise.all(Object.keys(need).map(function (k) {
            return PTF.embed(doc, need[k].id, { bold: need[k].bold, subset: true })
                .then(function (fnt) { fonts[k] = fnt; })
                .catch(function (e) { throw faceFailed(e); });
        })).then(function () {
            res.font = function (it, faceId) {
                return fonts[keyOf(it, faceId, opts)] || fonts[keyOf(it, null, opts)];
            };
        });
    }

    /* `it.face` — a face named by the item itself (the printable calendar sets
     * bold day numbers in the BODY face: bold is a weight there, not a heading). */
    function faceIdFor(it, opts) {
        if (it.face) return it.face;
        return (it.weight >= 600 ? opts.headingFace : opts.bodyFace) || opts.bodyFace || 'noto';
    }

    /* ── the public entry points ────────────────────────────────────────────*/

    /* One card, one or two pages, real page boxes. */
    function cardPdf(ctx) {
        return ensurePdfLib().then(function (PL) {
            return PL.PDFDocument.create().then(function (doc) {
                var g = pageGeom(ctx.boxes, ctx.opts);
                return prepare(doc, PL, ctx.sides, ctx.opts).then(function (res) {
                    ctx.sides.forEach(function (S) {
                        var page = doc.addPage([g.w * PT, g.h * PT]);
                        setBoxes(page, g);
                        var f = frame(g.h, g.origin);
                        drawItems(page, PL, f, S.items, res);
                        if (ctx.opts.marks) drawMarks(page, PL, g);
                    });
                    stampMeta(doc, ctx);
                    return doc.save();
                });
            });
        });
    }

    /* One design, many people, ONE file.
     *
     * A company ordering cards for its staff does not want twelve downloads
     * called "визитка (3).pdf" — it wants one document the shop can print, in a
     * known order, with a contents page it can check against the invoice.
     *
     * ⚠️ `prepare()` runs over EVERY person's items at once, not per person.
     * That is what makes the file affordable: the logo is embedded once and the
     * font subset covers the union of all the names, instead of twelve copies
     * of a 40 KB typeface. Per-person QR codes still differ and are keyed by
     * payload, so they do not collide. */
    function batchPdf(ctx) {
        return ensurePdfLib().then(function (PL) {
            return PL.PDFDocument.create().then(function (doc) {
                var g = pageGeom(ctx.boxes, ctx.opts);
                var all = [];
                ctx.people.forEach(function (p) { all = all.concat(p.sides); });
                return prepare(doc, PL, all, ctx.opts).then(function (res) {
                    ctx.people.forEach(function (p) {
                        p.sides.forEach(function (S) {
                            var page = doc.addPage([g.w * PT, g.h * PT]);
                            setBoxes(page, g);
                            drawItems(page, PL, frame(g.h, g.origin), S.items, res);
                            if (ctx.opts.marks) drawMarks(page, PL, g);
                        });
                    });
                    stampMeta(doc, ctx);
                    doc.setTitle((ctx.opts.title || 'Business cards') + ' — ' + ctx.people.length);
                    return doc.save();
                });
            });
        });
    }

    function setBoxes(page, g) {
        /* MediaBox ⊃ BleedBox ⊃ TrimBox. The shop's preflight reads these; a
         * file without them is "94 mm wide and trust me". */
        page.setMediaBox(0, 0, g.w * PT, g.h * PT);
        page.setBleedBox(g.extra * PT, g.extra * PT,
            (g.trimW + 2 * g.bleed) * PT, (g.trimH + 2 * g.bleed) * PT);
        page.setTrimBox(g.origin.x * PT, g.origin.y * PT, g.trimW * PT, g.trimH * PT);
    }

    function stampMeta(doc, ctx) {
        doc.setProducer('Timbrica business card maker');
        doc.setCreator('Timbrica');
        doc.setTitle(ctx.opts.title || 'Business card');
        /* The geometry, in words, for the human who opens the file at the shop.
         * Costs nothing and answers the first question they would email about. */
        doc.setSubject(
            'Trim ' + round1(ctx.boxes.w) + '×' + round1(ctx.boxes.h) + ' mm, ' +
            'bleed ' + round1(ctx.boxes.bleed) + ' mm, DeviceCMYK, ' +
            (ctx.opts.text === 'font' ? 'embedded subset fonts' : 'text converted to outlines'));
    }
    function round1(v) { return Math.round(v * 10) / 10; }

    /* ── many pages, each with its own size ─────────────────────────────────
     * The printable calendar (/printable-calendar) is the same item model as a
     * card — CMYK colour, text as curves or subset fonts, vector QR, CMYK logos —
     * but a document: a cover and twelve months, a quarterly header and its pads,
     * a tent stand next to its blocks. Every page carries its own trim and bleed,
     * so it gets its own boxes; `prepare()` still runs ONCE over all of them, so a
     * logo on thirteen pages is one XObject and the font subset is one subset.
     *
     * Three things a card never needed:
     *   group — items under one transformation, rotated 180° about (cx, cy): the
     *           back face of a folded tent reads upright only when printed upside
     *           down. One matrix, not a rotate argument on each primitive (see
     *           drawSlotCard for why).
     *   tile  — a poster cut into home-printer sheets: the poster's items drawn
     *           shifted by (dx, dy) and clipped to the sheet's printable window.
     *   jpeg  — a photograph embedded as a JPEG. A month photo as a CMYK bitmap
     *           under Flate is 15–35 MB per page at press resolution; twelve of them
     *           do not make a file anyone can send. For the print shop the caller
     *           hands CMYK JPEG bytes (`opts.jpegBytes[src]`: CardInk.photoCmyk +
     *           cmyk-jpeg.js, Adobe YCCK — DCTDecode/DeviceCMYK, ×1.5–2.8 the RGB
     *           size); without them the JPEG at `src` goes in as it is (home print).
     *
     * And one thing a print shop never needs: `opts.correction` — the printer
     * correction of label-maker/sheets.js (scale about the sheet centre, then
     * dx/dy mm), applied to the whole home sheet under one matrix, as sheetPdf
     * does: a driver that shrinks the page shrinks all of it.
     */
    function pagesPdf(ctx) {
        return ensurePdfLib().then(function (PL) {
            return PL.PDFDocument.create().then(function (doc) {
                var flat = [];
                ctx.pages.forEach(function (P) {
                    flatten(P.items, flat);
                    if (P.tile) flatten(P.tile.items, flat);
                });
                return prepare(doc, PL, [{ items: flat }], ctx.opts).then(function (res) {
                    return prepareJpegs(doc, flat, res, ctx.opts.jpegBytes).then(function () {
                        var corr = ctx.opts.correction;
                        ctx.pages.forEach(function (P) {
                            var g = pageGeom({ w: P.w, h: P.h, bleed: P.bleed || 0 }, { marks: !!P.marks });
                            var page = doc.addPage([g.w * PT, g.h * PT]);
                            setBoxes(page, g);
                            var f = frame(g.h, g.origin);
                            var corrected = !!(corr && !P.bleed && (corr.scale !== 1 || corr.dx || corr.dy));
                            if (corrected) {
                                var kc = corr.scale || 1;
                                page.pushOperators(PL.pushGraphicsState(), PL.concatTransformationMatrix(
                                    kc, 0, 0, kc,
                                    (g.w * (1 - kc) / 2 + (corr.dx || 0)) * PT,
                                    (g.h * (1 - kc) / 2 - (corr.dy || 0)) * PT));
                            }
                            if (P.tile) {
                                var T = P.tile;
                                page.pushOperators(PL.pushGraphicsState());
                                if (T.clip) clipRect(page, PL, f.x(T.clip.x), f.y(T.clip.y, T.clip.h), f.len(T.clip.w), f.len(T.clip.h));
                                page.pushOperators(PL.concatTransformationMatrix(1, 0, 0, 1, T.dx * PT, -T.dy * PT));
                                drawList(page, PL, f, T.items, res);
                                page.pushOperators(PL.popGraphicsState());
                            }
                            drawList(page, PL, f, P.items, res);
                            if (P.marks) drawMarks(page, PL, g);
                            if (corrected) page.pushOperators(PL.popGraphicsState());
                        });
                        doc.setProducer('Timbrica printable calendar');
                        doc.setCreator('Timbrica');
                        doc.setTitle(ctx.opts.title || 'Calendar');
                        if (ctx.opts.subject) doc.setSubject(ctx.opts.subject);
                        if (ctx.opts.lang) { try { doc.setLanguage(ctx.opts.lang); } catch (e) {} }
                        return doc.save();
                    });
                });
            });
        });
    }
    function flatten(items, out) {
        (items || []).forEach(function (it) {
            if (it && it.type === 'group') flatten(it.items, out);
            else if (it) out.push(it);
        });
        return out;
    }
    function drawList(page, PL, f, items, res) {
        var run = [];
        function flush() { if (run.length) { drawItems(page, PL, f, run, res); run = []; } }
        (items || []).forEach(function (it) {
            if (!it) return;
            if (it.type === 'group') {
                flush();
                var X = f.x(it.cx), Y = f.y(it.cy);
                page.pushOperators(PL.pushGraphicsState());
                if (it.rot === 180) page.pushOperators(PL.concatTransformationMatrix(-1, 0, 0, -1, 2 * X, 2 * Y));
                drawList(page, PL, f, it.items, res);
                page.pushOperators(PL.popGraphicsState());
            } else if (it.type === 'jpeg') {
                flush();
                var img = res.jpegs && res.jpegs[it.src];
                if (img) page.drawImage(img, { x: f.x(it.x), y: f.y(it.y, it.h), width: f.len(it.w), height: f.len(it.h) });
            } else run.push(it);
        });
        flush();
    }
    function prepareJpegs(doc, items, res, given) {
        res.jpegs = {};
        var seen = {};
        var jobs = [];
        items.forEach(function (it) {
            if (it.type !== 'jpeg' || !it.src || seen[it.src]) return;
            seen[it.src] = 1;
            var bytes = given && given[it.src];
            var got = bytes ? Promise.resolve(bytes)
                : fetch(it.src).then(function (r) { return r.arrayBuffer(); }).then(function (buf) { return new Uint8Array(buf); });
            jobs.push(got.then(function (b) {
                return doc.embedJpg(b);
            }).then(function (emb) { res.jpegs[it.src] = emb; }, function () { /* preflight reported it */ }));
        });
        return Promise.all(jobs);
    }

    /* ── the home-printing sheet ────────────────────────────────────────────
     * Same card, repeated on A4 with cut guides. Two things make it usable
     * rather than decorative:
     *
     *   - the guides are HAIRLINE CROSSES at the corners, not a full grid. A
     *     printed grid line is impossible to cut exactly along: half the line
     *     stays on one card. Corner crosses sit in the waste and give a
     *     straight-edge two points to align to.
     *   - a printed 50 mm scale bar. Nothing inside a browser can observe what
     *     the print driver did, and "fit to page" silently shrinking the sheet
     *     by 4 % is the single commonest way home-printed cards come out the
     *     wrong size. A ruler against that bar answers it in two seconds.
     */
    function sheetPdf(ctx) {
        return ensurePdfLib().then(function (PL) {
            return PL.PDFDocument.create().then(function (doc) {
                var imp = ctx.imposition;
                var sheet = imp.sheet;
                /* `ctx.slotPages` — a DIFFERENT card in every slot: one array of
                 * sides per sheet, a side per slot (null leaves the slot empty).
                 * /gift-certificate needs it: every certificate carries its own
                 * QR and number. Without it each sheet repeats ONE side, which is
                 * what a business card is. */
                var pages = ctx.slotPages || ctx.sides.map(function (S) {
                    var row = [];
                    for (var n = 0; n < imp.rows * imp.cols; n++) row.push(S);
                    return row;
                });
                var all = [];
                pages.forEach(function (row) { row.forEach(function (S) { if (S) all.push(S); }); });
                return prepare(doc, PL, all, ctx.opts).then(function (res) {
                    pages.forEach(function (row, pageIndex) {
                        var page = doc.addPage([sheet.w * PT, sheet.h * PT]);
                        page.setMediaBox(0, 0, sheet.w * PT, sheet.h * PT);
                        /* Printer correction (label-maker/sheets.js correction()):
                         * scale about the sheet centre, then shift — the whole
                         * sheet under one matrix, guides included, because the
                         * driver moves all of it. `opts.correctionFor(pageIndex)`
                         * lets a duplex sheet move its BACK pages on their own:
                         * the duplex unit lands the back 1–3 mm off the front,
                         * and one shift for both sides never changes that. */
                        var corr = ctx.opts.correctionFor ? ctx.opts.correctionFor(pageIndex) : ctx.opts.correction;
                        if (corr && (corr.scale !== 1 || corr.dx || corr.dy)) {
                            var k = corr.scale || 1;
                            page.pushOperators(PL.pushGraphicsState(), PL.concatTransformationMatrix(
                                k, 0, 0, k,
                                (sheet.w * (1 - k) / 2 + (corr.dx || 0)) * PT,
                                (sheet.h * (1 - k) / 2 - (corr.dy || 0)) * PT));
                        }
                        /* The card's own bleed offset inside its slot: the slot
                         * is the BLEED box when bleeds are on, so the trim
                         * starts one bleed in. */
                        var inset = ctx.opts.bleeds ? ctx.boxes.bleed : 0;
                        /* The card's own footprint, upright, in millimetres. */
                        var cardW = ctx.boxes.w + 2 * inset;
                        var cardH = ctx.boxes.h + 2 * inset;
                        for (var r = 0; r < imp.rows; r++) {
                            for (var c = 0; c < imp.cols; c++) {
                                var S = row[r * imp.cols + c];
                                if (!S) continue;
                                var ox = imp.x0 + c * (imp.cardW + imp.gap);
                                var oy = imp.y0 + r * (imp.cardH + imp.gap);
                                drawSlotCard(page, PL, S, res, {
                                    turned: !!imp.rotated,
                                    x0: ox * PT,
                                    /* PDF y grows up: the slot's BOTTOM edge is
                                     * its top edge plus its own height on the
                                     * sheet, measured down from the sheet top. */
                                    y0: (sheet.h - oy - (imp.rotated ? cardW : cardH)) * PT,
                                    cardW: cardW, cardH: cardH, inset: inset,
                                });
                            }
                        }
                        if (ctx.opts.cutGuides !== false) drawCutGuides(page, PL, imp, ctx, sheet);
                        if (ctx.opts.ruler !== false) drawRuler(page, PL, sheet, ctx.opts.rulerCaption, ctx.opts.rulerAt);
                        if (corr && (corr.scale !== 1 || corr.dx || corr.dy)) page.pushOperators(PL.popGraphicsState());
                    });
                    stampMeta(doc, ctx);
                    return doc.save();
                });
            });
        });
    }

    /* ── one card in one slot of the sheet ──────────────────────────────────
     * Two things happen here that did not before, and each of them was a defect
     * on the shipped sheet.
     *
     * 1. THE CARD IS TURNED WHEN THE IMPOSITION SAYS IT IS.
     *    `imposition()` turns the card when that fits more of them on the sheet
     *    (a 90×50 card is 12-up on A4 turned against 10-up upright). Turning the
     *    SLOT without turning the artwork is what shipped: the geometry said
     *    50 mm of column and the drawing put 90 mm of card into it, so every
     *    card in a row overlapped the next one and the outermost ran off the
     *    page. The count — 12 — was right, which is why it passed review; the
     *    sheet was never rasterised and looked at.
     *
     *    The whole card goes under ONE transformation matrix rather than each
     *    primitive growing a `rotate` argument. Text baselines, image anchors,
     *    QR runs and outline paths all use different anchoring conventions, and
     *    rotating each of them separately is eight chances to be 90° or one
     *    height out. Under a CTM there is exactly one place to be wrong.
     *
     *    The matrix is `translate(x0 + cardH, y0) ∘ rotate(+90°)`. Card-local
     *    (0,0)…(cardW,cardH) then lands on (x0,y0)…(x0+cardH, y0+cardW), which
     *    is exactly the turned slot.
     *
     * 2. THE CARD IS CLIPPED TO ITS OWN SLOT.
     *    A full-bleed background is grown past the trim by one bleed, on
     *    purpose. On a sheet with no bleeds the neighbouring cards butt against
     *    each other, so that overhang prints ON THE NEIGHBOUR — a colour band
     *    down the left of one card lands as a stripe down the right edge of the
     *    card beside it. Invisible on the default sample, whose back is one
     *    flat colour, and wrong on every layout that is not.
     */
    function drawSlotCard(page, PL, S, res, s) {
        var w = (s.turned ? s.cardH : s.cardW) * PT;
        var h = (s.turned ? s.cardW : s.cardH) * PT;
        page.pushOperators(PL.pushGraphicsState());
        if (clipRect(page, PL, s.x0, s.y0, w, h) === false) {
            /* No clip helper in this build of pdf-lib: an unclipped card is
             * still a correct card, and the alternative is no sheet at all. */
        }
        if (s.turned) {
            page.pushOperators(
                PL.concatTransformationMatrix(0, 1, -1, 0, s.x0 + s.cardH * PT, s.y0)
            );
        } else {
            page.pushOperators(PL.concatTransformationMatrix(1, 0, 0, 1, s.x0, s.y0));
        }
        /* Inside the matrix the card is upright at the origin again and its own
         * page is `cardH` mm tall, so the ordinary top-left-origin frame
         * applies — the same one the single-card export uses. */
        drawItems(page, PL, frame(s.cardH, { x: s.inset, y: s.inset }), S.items, res);
        page.pushOperators(PL.popGraphicsState());
    }

    /* `re W n` — the clip is set from the CURRENT path and then the path is
     * discarded without painting. Feature-detected because it is the one part
     * of this file that reaches for operators pdf-lib does not wrap in a
     * documented API. */
    function clipRect(page, PL, x, y, w, h) {
        if (!PL.rectangle || !PL.clip || !PL.endPath) return false;
        page.pushOperators(PL.rectangle(x, y, w, h), PL.clip(), PL.endPath());
        return true;
    }

    function drawCutGuides(page, PL, imp, ctx, sheet) {
        var col = PL.cmyk(0, 0, 0, 0.55);
        var inset = ctx.opts.bleeds ? ctx.boxes.bleed : 0;
        /* `guideFrom` — where the mark starts, in mm from the sheet edge (default 0:
         * the old behaviour). A home printer does not print its outer 3–5 mm, so a
         * mark drawn from the very edge simply is not on the paper; /loyalty-card
         * starts its marks past that dead zone. */
        var from = ctx.opts.guideFrom || 0;
        var len = ctx.opts.guideLen || 2.6, t = 0.2 * PT;
        var xs = [], ys = [];
        /* ⚠️ `imp.cardW/cardH`, never `ctx.boxes.w/h`: on a turned imposition
         * the two differ by a 90° swap, and marks drawn from the upright trim
         * size point the guillotine at the wrong line. */
        for (var c = 0; c < imp.cols; c++) {
            var ox = imp.x0 + c * (imp.cardW + imp.gap) + inset;
            xs.push(ox, ox + imp.cardW);
        }
        for (var r = 0; r < imp.rows; r++) {
            var oy = imp.y0 + r * (imp.cardH + imp.gap) + inset;
            ys.push(oy, oy + imp.cardH);
        }
        /* Marks reach INTO the sheet margin, never across the card. */
        xs.forEach(function (x) {
            [[from, from + len], [sheet.h - from - len, sheet.h - from]].forEach(function (seg) {
                page.drawLine({
                    start: { x: x * PT, y: (sheet.h - seg[0]) * PT },
                    end:   { x: x * PT, y: (sheet.h - seg[1]) * PT },
                    thickness: t, color: col,
                });
            });
        });
        ys.forEach(function (y) {
            [[from, from + len], [sheet.w - from - len, sheet.w - from]].forEach(function (seg) {
                page.drawLine({
                    start: { x: seg[0] * PT, y: (sheet.h - y) * PT },
                    end:   { x: seg[1] * PT, y: (sheet.h - y) * PT },
                    thickness: t, color: col,
                });
            });
        });
    }

    function drawRuler(page, PL, sheet, caption, at) {
        var col = PL.cmyk(0, 0, 0, 0.5);
        /* `at` — {x, y} of the bar's left end in sheet mm from the top-left, for a
         * sheet whose bottom band belongs to a card (3 × 99 mm on A4). */
        var len = 50, x = at ? at.x : 12, y = at ? at.y : sheet.h - 6;
        page.drawLine({
            start: { x: x * PT, y: (sheet.h - y) * PT },
            end:   { x: (x + len) * PT, y: (sheet.h - y) * PT },
            thickness: 0.3 * PT, color: col,
        });
        for (var mm = 0; mm <= len; mm += 10) {
            var tall = (mm % 50 === 0);
            page.drawLine({
                start: { x: (x + mm) * PT, y: (sheet.h - y) * PT },
                end:   { x: (x + mm) * PT, y: (sheet.h - y + (tall ? 2.6 : 1.4)) * PT },
                thickness: 0.3 * PT, color: col,
            });
        }
        if (caption) {
            try {
                page.drawText(caption, {
                    x: (x + len + 4) * PT, y: (sheet.h - y - 1) * PT,
                    size: 6, color: col,
                });
            } catch (e) { /* built-in font cannot encode Cyrillic — the bar
                             alone still answers the question. */ }
        }
    }

    /* ── image loading ──────────────────────────────────────────────────────*/
    /* An SVG has no resolution, so it must not be rasterised at "its own size"
     * — it does not have one. It was, and the consequence was measured on
     * 2026-08-23: the same wordmark exported at 381 dpi when small and 95 dpi
     * when enlarged, while the preflight said nothing at all, because the
     * resolution check exempts anything flagged `vector`. That exemption was a
     * promise the exporter did not keep.
     *
     * So a resolution-free source is rasterised at the size it is PRINTED at:
     * `it.w` millimetres times the press standard, capped so a full-bleed logo
     * cannot allocate an unbounded canvas. A browser re-renders an SVG at
     * whatever size drawImage is given, so this costs nothing but arithmetic.
     *
     * A raster source keeps its own pixels: resampling a photo upward invents
     * detail and inflates the file, and the preflight already reports its true
     * resolution. */
    /* A short stable id per source, so two placements of the same logo share
     * one embedded object instead of two. */
    var _imgIds = {}, _imgN = 0;
    function imageId(src) {
        if (!_imgIds[src]) _imgIds[src] = 'i' + (++_imgN);
        return _imgIds[src];
    }

    var SVG_EXPORT_DPI = 600;
    var SVG_MAX_PX = 4000;

    function svgCanvasSize(img, it) {
        var mm = it && it.w ? it.w : 0;
        if (!mm) return null;
        var w = Math.round(mm / 25.4 * SVG_EXPORT_DPI);
        w = Math.max(img.naturalWidth || 1, Math.min(SVG_MAX_PX, w));
        var ratio = (img.naturalHeight && img.naturalWidth)
            ? img.naturalHeight / img.naturalWidth
            : (it.h && it.w ? it.h / it.w : 1);
        return { w: w, h: Math.max(1, Math.round(w * ratio)) };
    }

    var PHOTO_EXPORT_DPI = 300;
    var PHOTO_MAX_PX = 4200;

    function bakePhotoBytes(it) {
        var CP = window.CardPhoto;
        if (!CP || !it.src) return Promise.resolve(null);
        return new Promise(function (resolve) {
            var img = new Image();
            img.onload = function () {
                try {
                    var wPx = Math.round(it.w / 25.4 * PHOTO_EXPORT_DPI);
                    var scale = Math.min(1, PHOTO_MAX_PX / Math.max(wPx, 1));
                    var W = Math.max(1, Math.round(wPx * scale));
                    var H = Math.max(1, Math.round(W * it.h / it.w));
                    var cv = CP.bake(img, it.spec, it.place, it.colours, { w: W, h: H });
                    var g2 = cv.getContext('2d', { willReadFrequently: true });
                    var px = g2.getImageData(0, 0, W, H).data;
                    var conv = rgbaToCmyk(px, W, H);
                    if (conv) {
                        return resolve({
                            cmyk: true, id: 'bg' + hash32(it.pkey),
                            w: W, h: H, samples: conv.samples, alpha: conv.alpha,
                            exact: conv.exact, colours: conv.colours,
                        });
                    }
                    window.__canvasBlob(cv, 'image/png').then(function (b) {
                        return b.arrayBuffer();
                    }).then(function (buf) {
                        resolve({ bytes: new Uint8Array(buf), mime: 'image/png' });
                    }, function () { resolve(null); });
                } catch (e) { resolve(null); }
            };
            /* A background that will not decode is not a reason to refuse the
             * card: the rest of the layout is intact and preflight already
             * reports what is missing. */
            img.onerror = function () { resolve(null); };
            img.src = it.src;
        });
    }

    /* A short, stable name for the CMYK object. The bake key is long and full
     * of punctuation; PDF names are neither. */
    function hash32(str) {
        var h = 2166136261;
        for (var i = 0; i < str.length; i++) {
            h ^= str.charCodeAt(i);
            h = (h + (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)) >>> 0;
        }
        return h.toString(36);
    }

    function loadImageBytes(src, opts, it) {
        var isSvg = /^data:image\/svg/i.test(src);
        return new Promise(function (resolve, reject) {
            var img = new Image();
            img.onload = function () {
                try {
                    if (opts.logoMono) return resolve(toStencil(img, opts, isSvg ? it : null));
                    var size = isSvg ? svgCanvasSize(img, it) : null;
                    var canvas = document.createElement('canvas');
                    canvas.width = size ? size.w : img.naturalWidth;
                    canvas.height = size ? size.h : img.naturalHeight;
                    var g2 = canvas.getContext('2d', { willReadFrequently: true });
                    g2.drawImage(img, 0, 0, canvas.width, canvas.height);

                    /* Straight to CMYK samples: an embedded PNG would be
                     * /DeviceRGB, and one RGB object makes the whole file fail
                     * a print shop's "is this CMYK?" check. */
                    var px = g2.getImageData(0, 0, canvas.width, canvas.height).data;
                    var conv = rgbaToCmyk(px, canvas.width, canvas.height);
                    if (conv) {
                        return resolve({
                            cmyk: true, id: imageId(src),
                            w: canvas.width, h: canvas.height,
                            samples: conv.samples, alpha: conv.alpha,
                            exact: conv.exact, colours: conv.colours,
                        });
                    }
                    /* No ink model on the page (should not happen): an RGB PNG
                     * is worse than CMYK but far better than no logo. */
                    window.__canvasBlob(canvas, 'image/png').then(function (b) {
                        return b.arrayBuffer();
                    }).then(function (buf) {
                        resolve({ bytes: new Uint8Array(buf), mime: 'image/png' });
                    }, reject);
                } catch (e) { reject(e); }
            };
            img.onerror = function () { reject(new Error('logo failed to load')); };
            img.src = src;
        });
    }

    /* Threshold to a 1-bit stencil. Alpha decides where the logo IS; luminance
     * decides only for fully opaque pixels, so a black-on-white JPEG and a
     * transparent PNG both work without the user telling us which they have. */
    function toStencil(img, opts, svgItem) {
        var vec = svgItem ? svgCanvasSize(img, svgItem) : null;
        var W = vec ? Math.min(SVG_MAX_PX, vec.w) : Math.min(2400, img.naturalWidth);
        var H = Math.round(W * img.naturalHeight / img.naturalWidth);
        var c = document.createElement('canvas');
        c.width = W; c.height = H;
        var g = c.getContext('2d');
        g.drawImage(img, 0, 0, W, H);
        var d = g.getImageData(0, 0, W, H).data;
        var rowBytes = (W + 7) >> 3;
        var bits = new Uint8Array(rowBytes * H);
        var hasAlpha = false;
        for (var i = 3; i < d.length; i += 4) { if (d[i] < 250) { hasAlpha = true; break; } }
        for (var y = 0; y < H; y++) {
            for (var x = 0; x < W; x++) {
                var p = (y * W + x) * 4;
                var on = hasAlpha
                    ? d[p + 3] > 127
                    : (0.299 * d[p] + 0.587 * d[p + 1] + 0.114 * d[p + 2]) < 140;
                if (on) bits[y * rowBytes + (x >> 3)] |= (0x80 >> (x & 7));
            }
        }
        return { stencil: true, bits: bits, w: W, h: H, id: 'l' + W + 'x' + H };
    }

    return {
        PT: PT,
        MARK_MARGIN_MM: MARK_MARGIN_MM,
        pageGeom: pageGeom,
        cardPdf: cardPdf,
        batchPdf: batchPdf,
        sheetPdf: sheetPdf,
        pagesPdf: pagesPdf,
        ensurePdfLib: ensurePdfLib,
        ensureQr: ensureQr,
        qrMatrix: qrMatrix,
        qrPath: qrPath,
        roundRectPath: roundRectPath,
        toStencil: toStencil,
    };
});
